// eslint-disable-next-line import/no-named-as-default
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Alert, Col, Form, Input, InputNumber, Modal, Row, Select, Switch } from "antd";
import { AxiosError } from "axios";
import { EVA_RESEARCH_NAME } from "constants/app.constants";
import _isEqual from "lodash/isEqual";
import { setIsUpdateGeoCalculationJobModalOpen } from "store/features";
import { RootState } from "store/rootReducer";
import styled from "styled-components";

import { useLengthConversion } from "hooks/useUnitConversion";

import { getValidSticks, getValidWells } from "api/data";

import { usePostStickCalculationJob } from "../hooks/usePostStickCalculationJob";
import {
  getPrimarySourceDisplayName,
  useSurveyDataSources
} from "../hooks/useSurveyDataSources";
import { formatWellListForTextArea, resolveStickJobEntities } from "../utils";

const { TextArea } = Input;

export const UpdateStickGeoCalculationJobModal = () => {
  const dispatch = useDispatch();
  const { convert, getDisplayUnit } = useLengthConversion();

  const isUpdateGeoCalculationJobModalOpen = useSelector(
    (state: RootState) => state.organizationSettings.isUpdateGeoCalculationJobModalOpen
  );

  const selectedStickCalculationJob = useSelector(
    (state: RootState) => state.organizationSettings?.selectedStickCalculationJob
  );

  const [form] = Form.useForm();
  const [includeSampled3D, setIncludeSampled3D] = useState(true);
  const [includeXda, setIncludeXda] = useState(false);

  const updateStickJob = usePostStickCalculationJob();
  const [errors, setErrors] = useState<string>("");

  const isSavedFilterJob = !!selectedStickCalculationJob?.savedFilterId;
  const savedFilterName =
    selectedStickCalculationJob?.savedFilterName ??
    selectedStickCalculationJob?.savedFilterId ??
    "";

  // Set the form values to the selected geo job when the modal is opened
  useEffect(() => {
    if (form && isUpdateGeoCalculationJobModalOpen) {
      form.setFieldsValue({
        ...selectedStickCalculationJob,
        wellList: formatWellListForTextArea(selectedStickCalculationJob?.wellList),
        stickList: formatWellListForTextArea(selectedStickCalculationJob?.stickList),
        threeDvLeftWidth:
          selectedStickCalculationJob?.threeDvDimensions?.leftWidth != null
            ? convert(selectedStickCalculationJob.threeDvDimensions.leftWidth, 2)
            : undefined,
        threeDvRightWidth:
          selectedStickCalculationJob?.threeDvDimensions?.rightWidth != null
            ? convert(selectedStickCalculationJob.threeDvDimensions.rightWidth, 2)
            : undefined,
        threeDvUpHeight:
          selectedStickCalculationJob?.threeDvDimensions?.upHeight != null
            ? convert(selectedStickCalculationJob.threeDvDimensions.upHeight, 2)
            : undefined,
        threeDvDownHeight:
          selectedStickCalculationJob?.threeDvDimensions?.downHeight != null
            ? convert(selectedStickCalculationJob.threeDvDimensions.downHeight, 2)
            : undefined,
        xdaLeftWidth:
          selectedStickCalculationJob?.xdaDimensions?.leftWidth != null
            ? convert(selectedStickCalculationJob.xdaDimensions.leftWidth, 2)
            : undefined,
        xdaRightWidth:
          selectedStickCalculationJob?.xdaDimensions?.rightWidth != null
            ? convert(selectedStickCalculationJob.xdaDimensions.rightWidth, 2)
            : undefined,
        xdaUpHeight:
          selectedStickCalculationJob?.xdaDimensions?.upHeight != null
            ? convert(selectedStickCalculationJob.xdaDimensions.upHeight, 2)
            : undefined,
        xdaDownHeight:
          selectedStickCalculationJob?.xdaDimensions?.downHeight != null
            ? convert(selectedStickCalculationJob.xdaDimensions.downHeight, 2)
            : undefined
        // zones: selectedGeoCalculationJob?.zones?.map((z, idx) => `${idx}_map_${z}`) || []
      });
      setIncludeSampled3D(
        selectedStickCalculationJob?.calculate3DV == null ||
          selectedStickCalculationJob?.calculate3DV === true
      );
      setIncludeXda(selectedStickCalculationJob?.calculateWellSpacing || false);
    }
    setModelSource(selectedStickCalculationJob?.modelSource || "");
    setPrimarySource(selectedStickCalculationJob?.primarySource);
  }, [selectedStickCalculationJob, form, isUpdateGeoCalculationJobModalOpen, convert]);

  useEffect(() => {
    if (updateStickJob.isSuccess) {
      dispatch(setIsUpdateGeoCalculationJobModalOpen(false));
    }
  }, [updateStickJob.isSuccess]);

  const [modelSource, setModelSource] = useState("");
  const [primarySource, setPrimarySource] = useState<string | undefined>(undefined);
  const surveySources = useSurveyDataSources();
  const primarySourceLabel = getPrimarySourceDisplayName(primarySource, surveySources);

  //   useEffect(() => {
  //     if (modelSource === "") {
  //       getGroupedPlayZonesByPeriod().then((response) => {
  //         const treeData = convertToTreeData(response.data) ?? [];
  //         setPeriodKeys(response.data.map((p) => `period_${p.period}`));
  //         setTreeData(treeData);
  //       });
  //     } else {
  //       // Can't change zones to we don't really need to load full map layers, just the selected ones
  //       const treeData = selectedGeoCalculationJob.zones.map((zone, idx) => ({
  //         title: zone,
  //         value: `${idx}_map_${zone}`,
  //         key: `${idx}_map_${zone}`,
  //         type: `zone`,
  //         checkable: false
  //       }));
  //       setTreeData(treeData);
  //     }
  //   }, [modelSource]);

  // Limited to updating the well list for now
  async function handleOk() {
    try {
      const values = form.getFieldsValue();

      let wellList = selectedStickCalculationJob?.wellList ?? [];
      let stickList = selectedStickCalculationJob?.stickList ?? [];

      if (!isSavedFilterJob) {
        const result = await resolveStickJobEntities(
          values.wellList,
          values.stickList,
          getValidWells,
          getValidSticks
        );

        if ("error" in result) {
          setErrors(result.error);
          return;
        }
        wellList = result.wellList;
        stickList = result.stickList;
      }

      // If nothing changed, close the modal
      if (
        _isEqual(selectedStickCalculationJob.wellList, wellList) &&
        _isEqual(selectedStickCalculationJob.stickList, stickList)
      ) {
        handleClose();
        return;
      }

      setErrors("");
      await updateStickJob.mutateAsync({
        ...selectedStickCalculationJob,
        wellList,
        stickList
      });
    } catch (e) {
      const body = (e as AxiosError)?.response?.data;
      setErrors(
        typeof body === "string" && body ? body : "Error occurred while updating job"
      );
    }
  }

  const handleClose = () => {
    updateStickJob.reset();
    setErrors("");
    dispatch(setIsUpdateGeoCalculationJobModalOpen(false));
  };

  const requiredRule = { required: true, message: "This field is required" };

  const handleValuesChange = (changedValues) => {
    if ("calculate3DV" in changedValues) {
      setIncludeSampled3D(changedValues.calculate3DV);
    }
    if ("calculateWellSpacing" in changedValues) {
      setIncludeXda(changedValues.calculateWellSpacing);
    }
  };

  return (
    <RootContainer>
      <Modal
        closable={false}
        confirmLoading={updateStickJob.isLoading}
        destroyOnClose={true}
        okButtonProps={{ loading: updateStickJob.isLoading, className: "" }}
        maskClosable={false}
        onCancel={handleClose}
        onOk={handleOk}
        open={isUpdateGeoCalculationJobModalOpen}
        title={"Update Stick Calculation Job"}
        width={"700px"}>
        <Form
          preserve={false}
          form={form}
          initialValues={{
            ...selectedStickCalculationJob,
            wellList: formatWellListForTextArea(selectedStickCalculationJob?.wellList),
            stickList: formatWellListForTextArea(selectedStickCalculationJob?.stickList)
          }}
          name="edit"
          onFinish={handleOk}
          requiredMark={false}
          onValuesChange={handleValuesChange}>
          <Form.Item name="name" label={<Label>Name</Label>} rules={[requiredRule]}>
            <Input disabled={true} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={9}>
              <Form.Item
                name="threeDvLeftWidth"
                label={<Label>3DV Radius ({getDisplayUnit()})</Label>}
                rules={[requiredRule]}>
                <InputNumber
                  disabled={true}
                  precision={0}
                  style={{ width: "100%" }}
                  placeholder="Left"
                />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                name="threeDvRightWidth"
                // label={<Label>Right</Label>}
                rules={[requiredRule]}>
                <InputNumber
                  disabled={true}
                  precision={0}
                  style={{ width: "100%" }}
                  placeholder="Right"
                />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                name="threeDvUpHeight"
                // label={<Label>Up</Label>}
                rules={[requiredRule]}>
                <InputNumber
                  disabled={true}
                  precision={0}
                  style={{ width: "100%" }}
                  placeholder="Up"
                />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                name="threeDvDownHeight"
                // label={<Label>Down</Label>}
                rules={[requiredRule]}>
                <InputNumber
                  disabled={true}
                  precision={0}
                  style={{ width: "100%" }}
                  placeholder="Down"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={9}>
              <Form.Item
                name="xdaLeftWidth"
                label={<Label>XDA Radius ({getDisplayUnit()})</Label>}
                rules={[requiredRule]}>
                <InputNumber
                  disabled={true}
                  precision={0}
                  style={{ width: "100%" }}
                  placeholder="Left"
                />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                name="xdaRightWidth"
                // label={<Label>Right</Label>}
                rules={[requiredRule]}>
                <InputNumber
                  disabled={true}
                  precision={0}
                  style={{ width: "100%" }}
                  placeholder="Right"
                />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                name="xdaUpHeight"
                // label={<Label>Up</Label>}
                rules={[requiredRule]}>
                <InputNumber
                  disabled={true}
                  precision={0}
                  style={{ width: "100%" }}
                  placeholder="Up"
                />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                name="xdaDownHeight"
                // label={<Label>Down</Label>}
                rules={[requiredRule]}>
                <InputNumber
                  disabled={true}
                  precision={0}
                  style={{ width: "100%" }}
                  placeholder="Down"
                />
              </Form.Item>
            </Col>
          </Row>
          {isSavedFilterJob ? (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label={<Label>Saved Filter</Label>}>
                  <Input
                    disabled={true}
                    data-testid="saved-filter-name-display"
                    value={savedFilterName}
                  />
                </Form.Item>
              </Col>
            </Row>
          ) : (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="wellList" label={<Label>Well List</Label>}>
                  <TextArea autoSize={{ minRows: 10, maxRows: 10 }} />
                </Form.Item>
              </Col>
            </Row>
          )}
          {isSavedFilterJob ? (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label={<Label>Scenarios</Label>}>
                  <Input
                    disabled={true}
                    value={
                      selectedStickCalculationJob?.scenarioNames?.length > 0
                        ? selectedStickCalculationJob.scenarioNames.join(", ")
                        : `${selectedStickCalculationJob?.stickList?.length ?? 0} sticks`
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
          ) : (
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name="stickList" label={<Label>Stick List</Label>}>
                  <TextArea autoSize={{ minRows: 10, maxRows: 10 }} />
                </Form.Item>
              </Col>
            </Row>
          )}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label={<Label>Survey Source</Label>}>
                <Select
                  disabled={true}
                  value={primarySource || "gdcDB"}
                  options={[
                    { label: primarySourceLabel, value: primarySource || "gdcDB" }
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="modelSource" label={<Label>Geo Source</Label>}>
                <Select
                  data-testid="model-source-select"
                  disabled={true}
                  value={modelSource}
                  options={[{ label: EVA_RESEARCH_NAME, value: "" }].concat(
                    modelSource ? [{ label: modelSource, value: modelSource }] : []
                  )}
                  onChange={(e) => {
                    setModelSource(e);
                    // setSelectedIpdbZones([]);
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          {/* <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="zones" label={<Label>Zones</Label>}>
                <TreeSelect
                  disabled={true}
                  showSearch
                  suffixIcon={<Icon path={mdiChevronDown} size={1} />}
                  switcherIcon={
                    <SwitcherIcon>{<Icon path={mdiChevronDown} size={1} />}</SwitcherIcon>
                  }
                  data-testid="geo-zone-select"
                  value={selectedIpdbZones}
                  dropdownStyle={{ overflow: "auto" }}
                  placeholder="Please select"
                  allowClear
                  multiple
                  maxTagCount={10}
                  onChange={setSelectedIpdbZones}
                  treeDefaultExpandedKeys={periodKeys}
                  treeCheckable={true}
                  treeData={treeData}
                />
              </Form.Item>
            </Col>
          </Row> */}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="calculate3DV" label={<Label>Sampled 3D</Label>}>
                <Switch disabled={true} checked={includeSampled3D} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="calculateWellSpacing" label={<Label>Spacing</Label>}>
                <Switch disabled={true} checked={includeXda} />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        {(updateStickJob.isError || errors) && (
          <ErrorMessage
            type="error"
            message={`${errors || "Error occurred while creating job"}`}
          />
        )}
      </Modal>
    </RootContainer>
  );
};

const RootContainer = styled.div``;

const Label = styled.span`
  display: block;
  width: 110px;
`;

// const SwitcherIcon = styled.span`
//   transform: translateY(2px);
//   color: rgba(var(--color-text-rgb), 0.3);
// `;

const ErrorMessage = styled(Alert)`
  margin-bottom: 1em;
`;
